import { useEffect, useState } from "react";
import axios from "axios";
import { BACKEND_URL } from "@/lib/api";
import { CMS_FALLBACK } from "@/lib/cmsDefaults";

// Module-level caches so multiple components on the same page share one fetch
const _cache = new Map(); // slug -> blocks dict
const _inflight = new Map(); // slug -> Promise

/** Public, unauthenticated fetch of published CMS values for a single page. */
async function fetchPublishedBlocks(slug) {
  if (_cache.has(slug)) return _cache.get(slug);
  if (_inflight.has(slug)) return _inflight.get(slug);
  const p = axios
    .get(`${BACKEND_URL}/api/public/cms/${slug}`, { timeout: 10000 })
    .then((res) => {
      const merged = { ...(CMS_FALLBACK[slug] || {}), ...(res.data?.blocks || {}) };
      _cache.set(slug, merged);
      return merged;
    })
    .catch(() => {
      const fb = CMS_FALLBACK[slug] || {};
      _cache.set(slug, fb);
      return fb;
    })
    .finally(() => {
      _inflight.delete(slug);
    });
  _inflight.set(slug, p);
  return p;
}

/**
 * Hook: read published CMS values for a page.
 * Returns { c, loading } where `c` is a key->value map.
 * The map is pre-populated with code fallbacks so the page can render
 * immediately even before the API responds.
 */
export function useCmsContent(slug) {
  const [c, setC] = useState(() => _cache.get(slug) || CMS_FALLBACK[slug] || {});
  const [loading, setLoading] = useState(!_cache.has(slug));

  useEffect(() => {
    let cancelled = false;
    fetchPublishedBlocks(slug).then((blocks) => {
      if (!cancelled) {
        setC(blocks);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { c, loading };
}

/** Force a cache refresh — used by the editor after publish. */
export function invalidateCms(slug) {
  if (slug) _cache.delete(slug);
  else _cache.clear();
}

/** Helper: render **bold** / *italic* markers as HTML inline. */
export function renderMarkers(text) {
  if (typeof text !== "string") return "";
  return text
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
    .replace(/\*(.+?)\*/g, "<i>$1</i>");
}

/** Helper: substitute {extra_per_bag} / {bin_clean_price} tokens with
 * values pulled from the public Operations Settings (already exposed at
 * /api/settings). The CMS edits the wrapping copy; numbers stay in Ops. */
export function substitutePricingTokens(template, settings) {
  if (typeof template !== "string" || !settings) return template;
  return template
    .replace(/\{one_bag_price\}/g, settings.price_1_bag ?? 3)
    .replace(/\{two_bag_price\}/g, settings.price_2_bags ?? 5)
    .replace(/\{extra_per_bag\}/g, settings.price_extra_bag ?? 2)
    .replace(/\{bin_clean_price\}/g, settings.monthly_cleaning_price ?? 10);
}
